import { bannerState } from '../../studentApp/stores';
import {
  SMILEYS,
  studentAppModalState,
} from '../../studentApp/stores/studentAppModalStore';
import { userState } from '../../studentApp/stores/userStore';

const IMAGE_PATH = 'GMM-images/';
const SEASONAL_IMAGES_PATH = IMAGE_PATH + 'seasonalimages/';
const TOTAL_REWARD_IMAGES = 20;
const REWARD_IMAGE_PERCENT_STEP = 1 / TOTAL_REWARD_IMAGES - Number.EPSILON;

let smileyTimeoutTimer: NodeJS.Timeout | undefined;
let smileyIntervalTimer: NodeJS.Timeout | undefined;
let crowning = false;

const seasonalRewardImage = (filename: string): string =>
  SEASONAL_IMAGES_PATH + filename;

export const RewardImage = {
  SMILEY: IMAGE_PATH + 'Smiley.png',
  CROWN: IMAGE_PATH + 'crown.png',
  YOU_HAVE_THE_MOST_POINTS: IMAGE_PATH + 'youhavethemostpoints.svg',
  TURKEY: seasonalRewardImage('Turkey.png'),
  CANDYCANE: seasonalRewardImage('candycane.png'),
  SNOWFLAKE: seasonalRewardImage('snowflake.png'),
  PUMPKIN: seasonalRewardImage('pumpkin.png'),
  PUMPKIN2: seasonalRewardImage('pumpkin2.png'),
  HEART: seasonalRewardImage('heart.png'),
  PIE: seasonalRewardImage('pie.png'),
  PI: seasonalRewardImage('pi.png'),
  TACO: seasonalRewardImage('taco.png'),
};

enum Month {
  JANUARY = 0,
  FEBRUARY = 1,
  MARCH = 2,
  APRIL = 3,
  MAY = 4,
  JUNE = 5,
  JULY = 6,
  AUGUST = 7,
  SEPTEMBER = 8,
  OCTOBER = 9,
  NOVEMBER = 10,
  DECEMBER = 11,
}

enum DayOfWeek {
  SUNDAY = 0,
  MONDAY = 1,
  TUESDAY = 2,
  WEDNESDAY = 3,
  THURSDAY = 4,
  FRIDAY = 5,
  SATURDAY = 6,
}

interface SpecialtyRewardImage {
  filename: string;
  count: number;
}

interface FixedRewardImageSchedule {
  month: Month;
  startDay: number;
  endDay?: number;
  specialtyImages: SpecialtyRewardImage[];
}

interface RewardImageCount {
  image: string;
  count: number;
}

const specialty = (
  filename: string,
  count = TOTAL_REWARD_IMAGES / 2
): SpecialtyRewardImage => ({
  filename,
  count,
});

// Fixed-date special days are checked before the Tuesday fallback so a Tuesday
// with its own holiday image does not also become Taco Tuesday.
const FIXED_REWARD_IMAGE_SCHEDULE: FixedRewardImageSchedule[] = [
  {
    month: Month.JANUARY,
    startDay: 1,
    specialtyImages: [specialty('newyearsday.png')],
  },
  {
    month: Month.JANUARY,
    startDay: 4,
    specialtyImages: [specialty('spaghetti.png')],
  },
  {
    month: Month.JANUARY,
    startDay: 13,
    specialtyImages: [specialty('rubberducky.png')],
  },
  {
    month: Month.JANUARY,
    startDay: 19,
    specialtyImages: [specialty('popcorn.png')],
  },
  {
    month: Month.JANUARY,
    startDay: 31,
    specialtyImages: [specialty('hotchocolate.png')],
  },
  {
    month: Month.FEBRUARY,
    startDay: 2,
    specialtyImages: [specialty('groundhog.png')],
  },
  {
    month: Month.FEBRUARY,
    startDay: 9,
    specialtyImages: [specialty('pizza.png')],
  },
  {
    month: Month.FEBRUARY,
    startDay: 12,
    endDay: 14,
    specialtyImages: [specialty('heart.png')],
  },
  {
    month: Month.FEBRUARY,
    startDay: 27,
    specialtyImages: [specialty('polarbear.png')],
  },
  {
    month: Month.MARCH,
    startDay: 1,
    specialtyImages: [specialty('pig.png')],
  },
  {
    month: Month.MARCH,
    startDay: 14,
    specialtyImages: [specialty('pie.png', 7), specialty('pi.png', 7)],
  },
  {
    month: Month.MARCH,
    startDay: 17,
    specialtyImages: [specialty('shamrock.png')],
  },
  {
    month: Month.MARCH,
    startDay: 19,
    endDay: 21,
    specialtyImages: [
      specialty('flower1.png', 3),
      specialty('flower2.png', 3),
      specialty('flower3.png', 3),
      specialty('flower4.png', 3),
    ],
  },
  {
    month: Month.MARCH,
    startDay: 23,
    specialtyImages: [specialty('puppy.png')],
  },
  {
    month: Month.MARCH,
    startDay: 30,
    specialtyImages: [specialty('pencil.png')],
  },
  {
    month: Month.APRIL,
    startDay: 1,
    specialtyImages: [specialty('jester.png')],
  },
  {
    month: Month.APRIL,
    startDay: 9,
    specialtyImages: [specialty('unicorn.png')],
  },
  {
    month: Month.APRIL,
    startDay: 14,
    specialtyImages: [specialty('dolphin.png')],
  },
  {
    month: Month.APRIL,
    startDay: 22,
    specialtyImages: [specialty('earth.png')],
  },
  {
    month: Month.APRIL,
    startDay: 26,
    specialtyImages: [specialty('pretzel.png')],
  },
  {
    month: Month.MAY,
    startDay: 5,
    specialtyImages: [specialty('sombrero.png')],
  },
  {
    month: Month.MAY,
    startDay: 13,
    specialtyImages: [specialty('applepie.png')],
  },
  {
    month: Month.MAY,
    startDay: 20,
    specialtyImages: [specialty('bee.png')],
  },
  {
    month: Month.MAY,
    startDay: 23,
    specialtyImages: [
      specialty('taffy1.png', 3),
      specialty('taffy2.png', 3),
      specialty('taffy3.png', 3),
      specialty('taffy4.png', 3),
    ],
  },
  {
    month: Month.JUNE,
    startDay: 4,
    specialtyImages: [specialty('cheese.png')],
  },
  {
    month: Month.JUNE,
    startDay: 11,
    specialtyImages: [specialty('corn.png')],
  },
  {
    month: Month.JUNE,
    startDay: 14,
    specialtyImages: [specialty('flag.png')],
  },
  {
    month: Month.JUNE,
    startDay: 20,
    endDay: 22,
    specialtyImages: [specialty('sun.png')],
  },
  {
    month: Month.JUNE,
    startDay: 27,
    specialtyImages: [
      specialty('sunglasses1.png', 4),
      specialty('sunglasses2.png', 4),
      specialty('sunglasses3.png', 4),
    ],
  },
  {
    month: Month.JULY,
    startDay: 4,
    specialtyImages: [
      specialty('fireworks1.png', 3),
      specialty('fireworks2.png', 3),
      specialty('fireworks3.png', 3),
      specialty('fireworks4.png', 3),
    ],
  },
  {
    month: Month.JULY,
    startDay: 10,
    specialtyImages: [specialty('cat.png')],
  },
  {
    month: Month.JULY,
    startDay: 16,
    specialtyImages: [specialty('cherries.png')],
  },
  {
    month: Month.JULY,
    startDay: 20,
    specialtyImages: [
      specialty('lollipop1.png', 3),
      specialty('lollipop2.png', 3),
      specialty('lollipop3.png', 3),
      specialty('lollipop4.png', 3),
    ],
  },
  {
    month: Month.JULY,
    startDay: 29,
    specialtyImages: [specialty('tiger.png')],
  },
  {
    month: Month.AUGUST,
    startDay: 3,
    specialtyImages: [specialty('watermelon.png')],
  },
  {
    month: Month.AUGUST,
    startDay: 10,
    specialtyImages: [specialty('smore.png')],
  },
  {
    month: Month.AUGUST,
    startDay: 19,
    specialtyImages: [specialty('potato.png')],
  },
  {
    month: Month.AUGUST,
    startDay: 25,
    specialtyImages: [specialty('bananasplit.png')],
  },
  {
    month: Month.SEPTEMBER,
    startDay: 9,
    specialtyImages: [specialty('teddybear.png')],
  },
  {
    month: Month.SEPTEMBER,
    startDay: 18,
    specialtyImages: [specialty('cheeseburger.png')],
  },
  {
    month: Month.SEPTEMBER,
    startDay: 21,
    endDay: 24,
    specialtyImages: [specialty('leaf1.png', 5), specialty('leaf2.png', 5)],
  },
  {
    month: Month.SEPTEMBER,
    startDay: 26,
    specialtyImages: [specialty('pancakes.png')],
  },
  {
    month: Month.OCTOBER,
    startDay: 5,
    specialtyImages: [specialty('teacher.png')],
  },
  {
    month: Month.OCTOBER,
    startDay: 16,
    specialtyImages: [
      specialty('sports1.png', 3),
      specialty('sports2.png', 3),
      specialty('sports3.png', 3),
      specialty('sports4.png', 3),
    ],
  },
  {
    month: Month.OCTOBER,
    startDay: 21,
    specialtyImages: [specialty('apple1.png', 5), specialty('apple2.png', 5)],
  },
  {
    month: Month.OCTOBER,
    startDay: 28,
    endDay: 31,
    specialtyImages: [
      specialty('pumpkin.png', 7),
      specialty('pumpkin2.png', 7),
    ],
  },
  {
    month: Month.NOVEMBER,
    startDay: 4,
    specialtyImages: [
      specialty('candy1.png', 3),
      specialty('candy2.png', 3),
      specialty('candy3.png', 3),
      specialty('candy4.png', 3),
    ],
  },
  {
    month: Month.NOVEMBER,
    startDay: 11,
    specialtyImages: [specialty('star.png')],
  },
  {
    month: Month.NOVEMBER,
    startDay: 14,
    specialtyImages: [specialty('pickles.png')],
  },
  {
    month: Month.DECEMBER,
    startDay: 4,
    specialtyImages: [specialty('cookie.png')],
  },
  {
    month: Month.DECEMBER,
    startDay: 7,
    specialtyImages: [
      specialty('cottoncandy1.png', 5),
      specialty('cottoncandy2.png', 5),
    ],
  },
  {
    month: Month.DECEMBER,
    startDay: 15,
    specialtyImages: [
      specialty('cupcake1.png', 3),
      specialty('cupcake2.png', 3),
      specialty('cupcake3.png', 3),
      specialty('cupcake4.png', 3),
    ],
  },
  {
    month: Month.DECEMBER,
    startDay: 16,
    endDay: 17,
    specialtyImages: [
      specialty('candycane.png', 1),
      specialty('snowflake.png', 1),
    ],
  },
  {
    month: Month.DECEMBER,
    startDay: 18,
    endDay: 19,
    specialtyImages: [
      specialty('candycane.png', 2),
      specialty('snowflake.png', 2),
    ],
  },
  {
    month: Month.DECEMBER,
    startDay: 20,
    specialtyImages: [
      specialty('candycane.png', 4),
      specialty('snowflake.png', 4),
    ],
  },
  {
    month: Month.DECEMBER,
    startDay: 21,
    specialtyImages: [
      specialty('candycane.png', 6),
      specialty('snowflake.png', 6),
    ],
  },
  {
    month: Month.DECEMBER,
    startDay: 22,
    specialtyImages: [
      specialty('candycane.png', 8),
      specialty('snowflake.png', 8),
    ],
  },
  {
    month: Month.DECEMBER,
    startDay: 23,
    endDay: 25,
    specialtyImages: [specialty('candycane.png'), specialty('snowflake.png')],
  },
  {
    month: Month.DECEMBER,
    startDay: 31,
    specialtyImages: [specialty('nye1.png', 5), specialty('nye2.png', 5)],
  },
];

function getScheduledRewardImages(
  date: Date,
  coreImage: string
): string[] | undefined {
  const movableRewardImages = getMovableRewardImageCounts(date, coreImage);

  if (movableRewardImages) {
    return buildRewardImagesFromCounts(movableRewardImages);
  }

  const fixedRewardImageSchedule = getFixedRewardImageSchedule(date);

  if (fixedRewardImageSchedule) {
    return buildRewardImagesFromSpecialties(
      coreImage,
      fixedRewardImageSchedule.specialtyImages
    );
  }

  if (date.getDay() === DayOfWeek.TUESDAY) {
    return buildRewardImagesFromSpecialties(coreImage, [specialty('taco.png')]);
  }

  return undefined;
}

function getMovableRewardImageCounts(
  date: Date,
  coreImage: string
): RewardImageCount[] | undefined {
  const month = date.getMonth();
  const dayOfMonth = date.getDate();

  if (month === Month.MAY && dayOfMonth === getMemorialDay(date)) {
    return buildRewardImageCounts(coreImage, [specialty('ribbon.png')]);
  }

  if (month === Month.SEPTEMBER && dayOfMonth === getLaborDay(date)) {
    return buildRewardImageCounts(coreImage, [specialty('hardhat.png')]);
  }

  if (month !== Month.NOVEMBER) return undefined;

  const daysBeforeThanksgiving = getThanksgivingDay(date) - dayOfMonth;

  if (daysBeforeThanksgiving > 7 || daysBeforeThanksgiving < 0) {
    return undefined;
  }

  if (daysBeforeThanksgiving >= 6) {
    return buildRewardImageCounts(coreImage, [specialty('Turkey.png', 4)]);
  }

  if (daysBeforeThanksgiving >= 4) {
    return buildRewardImageCounts(coreImage, [specialty('Turkey.png')]);
  }

  if (daysBeforeThanksgiving >= 2) {
    return buildRewardImageCounts(coreImage, [specialty('Turkey.png', 14)]);
  }

  return buildRewardImageCounts(coreImage, [specialty('Turkey.png', 20)]);
}

function getFixedRewardImageSchedule(
  date: Date
): FixedRewardImageSchedule | undefined {
  const month = date.getMonth();
  const dayOfMonth = date.getDate();

  return FIXED_REWARD_IMAGE_SCHEDULE.find(
    schedule =>
      month === schedule.month &&
      dayOfMonth >= schedule.startDay &&
      dayOfMonth <= (schedule.endDay ?? schedule.startDay)
  );
}

function buildRewardImagesFromSpecialties(
  coreImage: string,
  specialtyImages: SpecialtyRewardImage[]
): string[] {
  return buildRewardImagesFromCounts(
    buildRewardImageCounts(coreImage, specialtyImages)
  );
}

function buildRewardImageCounts(
  coreImage: string,
  specialtyImages: SpecialtyRewardImage[]
): RewardImageCount[] {
  const specialtyImageCount = specialtyImages.reduce(
    (count, image) => count + image.count,
    0
  );
  const coreImageCount = TOTAL_REWARD_IMAGES - specialtyImageCount;
  const imageCounts = specialtyImages.map(({ filename, count }) => ({
    image: seasonalRewardImage(filename),
    count,
  }));

  return [{ image: coreImage, count: coreImageCount }, ...imageCounts];
}

function buildRewardImagesFromCounts(
  imageCounts: RewardImageCount[]
): string[] {
  const rewardImages = Array<string>();

  imageCounts.forEach(({ image, count }) => {
    for (let index = 0; index < count; index++) {
      rewardImages.push(image);
    }
  });

  return rewardImages;
}

function buildRewardImagesFromPool(
  rewardImagePool: string[],
  percentOfImages?: number[]
): string[] {
  if (!percentOfImages) {
    percentOfImages = [];
    const inc = 1 / rewardImagePool.length;

    for (let z = inc; z < 1; z += inc) {
      percentOfImages.push(z);
    }
  }

  let nextPercent = 0;
  const rewardImages = Array<string>();

  for (let a = 0; a < TOTAL_REWARD_IMAGES; a++) {
    let rewardImage = undefined;

    nextPercent += REWARD_IMAGE_PERCENT_STEP;

    for (let x = 0; x < rewardImagePool.length - 1; x++) {
      if (nextPercent < percentOfImages[x]) {
        rewardImage = rewardImagePool[x];
        break;
      }
    }

    if (!rewardImage) rewardImage = rewardImagePool[rewardImagePool.length - 1];

    rewardImages.push(rewardImage);
  }

  return rewardImages;
}

function getMemorialDay(date: Date): number {
  return getLastWeekdayOfMonth(date.getFullYear(), Month.MAY, DayOfWeek.MONDAY);
}

function getLaborDay(date: Date): number {
  return getNthWeekdayOfMonth(
    date.getFullYear(),
    Month.SEPTEMBER,
    DayOfWeek.MONDAY,
    1
  );
}

function getThanksgivingDay(date: Date): number {
  return getNthWeekdayOfMonth(
    date.getFullYear(),
    Month.NOVEMBER,
    DayOfWeek.THURSDAY,
    4
  );
}

function getNthWeekdayOfMonth(
  year: number,
  month: Month,
  dayOfWeek: DayOfWeek,
  occurrence: number
): number {
  const firstDayOfMonth = new Date(year, month, 1).getDay();
  const daysUntilFirstWeekday = (dayOfWeek - firstDayOfMonth + 7) % 7;

  return 1 + daysUntilFirstWeekday + 7 * (occurrence - 1);
}

function getLastWeekdayOfMonth(
  year: number,
  month: Month,
  dayOfWeek: DayOfWeek
): number {
  const lastDayOfMonthDate = new Date(year, month + 1, 0);
  const daysSinceLastWeekday =
    (lastDayOfMonthDate.getDay() - dayOfWeek + 7) % 7;

  return lastDayOfMonthDate.getDate() - daysSinceLastWeekday;
}

/**
 *
 * @param rewardImagePool relative paths to images to use for this session
 * @param percentOfImages percentage of each image to use (of our '20 smileys')
 */
export function setRewardImage(
  rewardImagePool?: string[],
  percentOfImages?: number[]
): void {
  const rewardImages = getRewardImage(
    new Date(),
    rewardImagePool,
    percentOfImages
  );

  updateRewardUI(rewardImages);
}

export function getRewardImage(
  date: Date,
  rewardImagePool?: string[],
  percentOfImages?: number[]
): string[] {
  const coreImage = crowning ? RewardImage.CROWN : RewardImage.SMILEY;

  if (!rewardImagePool) {
    const scheduledRewardImages = getScheduledRewardImages(date, coreImage);

    if (scheduledRewardImages) return scheduledRewardImages;

    rewardImagePool = [coreImage];
  }

  return buildRewardImagesFromPool(rewardImagePool, percentOfImages);
}

function updateRewardUI(rewardImages: string[]): void {
  rewardImages.forEach((rewardImage, index) => {
    const imageElement =
      "<img id='smiley" +
      index +
      "' src='" +
      rewardImage +
      "' style='position:fixed'>";

    $('#smileys').append(imageElement);
  });

  if (crowning) {
    // Remove any previous #mostPoints image
    $('#smileys #mostPoints').remove();

    // Create the image element
    const img = document.createElement('img');

    img.id = 'mostPoints';
    img.src = RewardImage.YOU_HAVE_THE_MOST_POINTS;
    img.style.position = 'fixed';
    img.style.visibility = 'hidden'; // Hide until loaded

    img.onload = function () {
      const w = document.documentElement.clientWidth;
      const h = document.documentElement.clientHeight;

      img.style.width = Math.max(w / 2, 300) + 'px';
      img.style.top = h / 2 - img.height / 2 + 'px';
      img.style.left = w / 2 - img.width / 2 + 'px';
      img.style.transform = 'scale(2)';
      img.style.transition = 'transform 1.2s';
      img.style.visibility = 'visible';
    };

    const smileysDiv = document.getElementById('smileys');

    if (smileysDiv) {
      smileysDiv.appendChild(img);
    }
  }
}

export function scatterSmileys(): void {
  const w = document.documentElement.clientWidth;
  const h = document.documentElement.clientHeight;

  $('#smileys')
    .children()
    .each(function () {
      const $el = $(this);

      // scatter images (other than 'mostPoints' which will be centered)
      if ($el.attr('id') !== 'mostPoints') {
        const x = getInt(-10, w - $el.width()! + 10);
        const y = getInt(-10, h - $el.height()! - 10);

        // @ts-ignore No idea why this is set
        $el[0].gmmY = y;
        $el.css('left', x + 'px');
        $el.css('top', y + 'px');
      }
    });
}

export function showSmileys(): void {
  const pointsToday = bannerState().pointsToday;
  const updatedCrowning =
    pointsToday > 0 && bannerState().classHighestToday <= pointsToday;

  if (updatedCrowning != crowning) {
    crowning = updatedCrowning && !userState().neverCrown;
    setRewardImage();
  }

  studentAppModalState().setCurrentModal(SMILEYS);

  const stopSmileys = (): void => {
    studentAppModalState().stopSmileys();
    turnOffSmileyTimers();
  };

  // In case there is a prior smiley animation running ("shouldn't" be possible)
  turnOffSmileyTimers();

  smileyIntervalTimer = setInterval(moveSmileys, 100);
  smileyTimeoutTimer = setTimeout(stopSmileys, 2000);
}

function moveSmileys(): void {
  const clientHeight = document.documentElement.clientHeight;
  const inc = clientHeight * 0.05;

  $('#smileys')
    .children()
    .each(function () {
      const $el = $(this);

      // images (other than 'mostPoints') will "rain" down
      if ($el.attr('id') !== 'mostPoints') {
        // @ts-ignore No idea why this is set
        let r = $el[0].gmmY || 0;

        r += inc;

        if (r > clientHeight) {
          r = -10;
          const l = getInt(
            0,
            document.documentElement.clientWidth - $el.width()!
          );

          $el.css('left', l + 'px');
        }

        // @ts-ignore No idea why this is set
        $el[0].gmmY = r;
        $el.css('top', r + 'px');
      }
    });
}

export function turnOffSmileyTimers(): void {
  clearInterval(smileyIntervalTimer);
  clearTimeout(smileyTimeoutTimer);
}

function getInt(a: number, b: number): number {
  return Math.floor(Math.random() * (b - a + 1) + a);
}
